import { toast } from "react-toastify";
import { EnquirySiteVisitStatus } from "./constants/enquiry-site-visit.constants";
import { normalizeSiteVisitStatus } from "./utils/siteVisitActionPolicy";
import {
  cancelEnquirySiteVisit,
  completeEnquirySiteVisit,
  startEnquirySiteVisit,
} from "./services/EnquirySiteVisitService";

export function enquirySiteVisitWorkflowErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return ax.response?.data?.message || ax.message || "Request failed";
}

export type EnquirySiteVisitWorkflowFlags = {
  canStart: boolean;
  canComplete: boolean;
  canCancel: boolean;
};

export type EnquirySiteVisitWorkflowOptions = {
  /** When true, the current user is an employee (see `useProfile().isEmployee`). */
  allowStartCompleteForActor?: boolean;
};

export function getEnquirySiteVisitWorkflowFlags(
  status: string | undefined | null,
  options?: EnquirySiteVisitWorkflowOptions,
): EnquirySiteVisitWorkflowFlags {
  const st = normalizeSiteVisitStatus(status);
  const allow = options?.allowStartCompleteForActor === true;
  const nonTerminal =
    st !== EnquirySiteVisitStatus.COMPLETED &&
    st !== EnquirySiteVisitStatus.CANCELLED;
  return {
    canStart: allow && st === EnquirySiteVisitStatus.NEW,
    canComplete: allow && st === EnquirySiteVisitStatus.IN_PROGRESS,
    canCancel: allow && nonTerminal,
  };
}

export async function runEnquirySiteVisitStart(id: number): Promise<boolean> {
  try {
    const res = await startEnquirySiteVisit(id);
    if (res.success) {
      toast.success(res.message || "Visit started");
      return true;
    }
    toast.error(res.message || "Could not start visit");
    return false;
  } catch (e) {
    toast.error(enquirySiteVisitWorkflowErrorMessage(e));
    return false;
  }
}

export async function runEnquirySiteVisitComplete(
  id: number,
  body: { description?: string },
): Promise<boolean> {
  try {
    const res = await completeEnquirySiteVisit(id, body);
    if (res.success) {
      toast.success(res.message || "Visit completed");
      return true;
    }
    toast.error(res.message || "Could not complete visit");
    return false;
  } catch (e) {
    toast.error(enquirySiteVisitWorkflowErrorMessage(e));
    return false;
  }
}

export async function runEnquirySiteVisitCancel(
  id: number,
  body: { cancellationReason?: string | null },
): Promise<boolean> {
  try {
    const res = await cancelEnquirySiteVisit(id, body);
    if (res.success) {
      toast.success(res.message || "Visit cancelled");
      return true;
    }
    toast.error(res.message || "Could not cancel visit");
    return false;
  } catch (e) {
    toast.error(enquirySiteVisitWorkflowErrorMessage(e));
    return false;
  }
}
